import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../services/common.service';
import StorageService from '../../services/StorageService';
import { API_URLS, API_BASE_URL } from '../../constants/api-urls';
import { useLocationMaster } from '../../hooks/useLocationMaster';
import './RequestInspectionQuotation.scss';

const API_BASE_URL_MEDIA = API_BASE_URL.replace('/api/v1', '');
const getMediaUrl = (accessPath, auctionId = null) => {
  if (!accessPath) return '';
  const path = typeof accessPath === 'string' ? accessPath : (accessPath && accessPath.path ? accessPath.path : '');
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/auction-media/')) return `${API_BASE_URL_MEDIA}${path}`;
  const fileName = path.split(/[/\\]/).pop();
  if (fileName && auctionId) return `${API_BASE_URL_MEDIA}/auction-media/${auctionId}/${fileName}`;
  return `${API_BASE_URL_MEDIA}/media/${fileName || ''}`;
};
const getFirstProductImageUrl = (auction) => {
  if (!auction?.images) return null;
  let arr = [];
  if (Array.isArray(auction.images)) arr = auction.images;
  else if (typeof auction.images === 'string') {
    try { arr = auction.images.trim().startsWith('[') ? JSON.parse(auction.images) : [auction.images]; } catch (_) { arr = [auction.images]; }
  }
  const first = arr[0];
  if (!first) return null;
  return getMediaUrl(first, auction.auction_id);
};

export default function RequestInspectionQuotation() {
  const { auctionId } = useParams();
  const navigate = useNavigate();
  const [auction, setAuction] = useState(null);
  const [winningBid, setWinningBid] = useState(null);
  const [sellerProfile, setSellerProfile] = useState(null);
  const [buyerProfile, setBuyerProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [availableQAs, setAvailableQAs] = useState([]);
  const [qaMatchLevel, setQaMatchLevel] = useState(null); // 'pincode', 'city', or 'state'
  const [loadingQAs, setLoadingQAs] = useState(false);
  
  const [formData, setFormData] = useState({
    preferred_date: '',
    preferred_time: '',
    inspection_location_address: '',
    inspection_location_city: '',
    inspection_location_state: '',
    inspection_location_pincode: '', // Store pincode in formData
    inspection_checklist: [
      'Verify product condition matches description',
      'Check for any visible damages or defects',
      'Test functionality and performance',
      'Document with photographs and videos'
    ],
    special_instructions: '',
    contact_name: '',
    contact_phone: '',
    contact_email: ''
  });

  const [customChecklistItem, setCustomChecklistItem] = useState('');

  const { countries, states, cities, updateListsForCountry, updateListsForState } = useLocationMaster();

  useEffect(() => {
    fetchData();
  }, [auctionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch auction
      const auctionResponse = await CommonService.get(`${API_URLS.AUCTION.BASE}/${auctionId}`);
      if (auctionResponse && auctionResponse.success) {
        setAuction(auctionResponse.data);
        
        // Load location data if needed
        if (auctionResponse.data.country_id) {
          await updateListsForCountry(auctionResponse.data.country_id);
          if (auctionResponse.data.state_id) {
            await updateListsForState(auctionResponse.data.country_id, auctionResponse.data.state_id);
          }
        }
      }

      // Fetch bids to get winning bid
      const bidsResponse = await CommonService.get(API_URLS.AUCTION.BIDS(auctionId));
      if (bidsResponse && bidsResponse.success && bidsResponse.data) {
        const bids = Array.isArray(bidsResponse.data) ? bidsResponse.data : [];
        const bid = bids.find(b => 
          b.bid_id === auctionResponse.data.winning_bid_id || 
          b.bid_status === 'won'
        );
        if (bid) {
          setWinningBid(bid);
        }
      }

      // Fetch buyer profile
      const user = StorageService.getUser();
      if (user && user.user_login_id) {
        try {
          const buyerResponse = await CommonService.get(`/company/profile/${user.user_login_id}`);
          if (buyerResponse && buyerResponse.success) {
            setBuyerProfile(buyerResponse.data);
            // Pre-fill contact details
            const contactDetails = buyerResponse.data.contact_details 
              ? (typeof buyerResponse.data.contact_details === 'string' 
                  ? JSON.parse(buyerResponse.data.contact_details) 
                  : buyerResponse.data.contact_details)
              : {};
            setFormData(prev => ({
              ...prev,
              contact_name: contactDetails.contact_name || user.name || '',
              contact_phone: contactDetails.phone || user.phone || '',
              contact_email: user.email || ''
            }));
          }
        } catch (err) {
          console.error('Error fetching buyer profile:', err);
        }
      }

      // Fetch seller profile
      if (auctionResponse.data.user_login_id) {
        try {
          const sellerResponse = await CommonService.get(`/company/profile/${auctionResponse.data.user_login_id}`);
          if (sellerResponse && sellerResponse.success && sellerResponse.data) {
            console.log('Seller profile fetched:', sellerResponse.data);
            setSellerProfile(sellerResponse.data);
            
            // Pre-fill inspection location from seller's address
            if (sellerResponse.data.address) {
              try {
                const address = typeof sellerResponse.data.address === 'string' 
                  ? JSON.parse(sellerResponse.data.address) 
                  : sellerResponse.data.address;
                
                // Build full address string
                const addressParts = [];
                if (address.address) addressParts.push(address.address);
                if (address.landmark && address.landmark !== 'N/A') addressParts.push(address.landmark);
                const fullAddress = addressParts.join(', ');
                
                const sellerPincode = address.pincode ? String(address.pincode).trim() : null;
                
                setFormData(prev => ({
                  ...prev,
                  inspection_location_address: fullAddress || prev.inspection_location_address,
                  inspection_location_city: address.city || prev.inspection_location_city,
                  inspection_location_state: address.state || prev.inspection_location_state,
                  inspection_location_pincode: sellerPincode || prev.inspection_location_pincode
                }));
                
                // Fetch available QAs after setting location (pincode, city, state)
                console.log('Seller address parsed:', { 
                  pincode: sellerPincode, 
                  city: address.city, 
                  state: address.state,
                  fullAddress: address 
                });
                fetchAvailableQAs(sellerPincode, address.city, address.state);
              } catch (e) {
                console.error('Error parsing seller address for form:', e);
                // Still try to fetch QAs with available location data
                if (auctionResponse.data.city || auctionResponse.data.state) {
                  const auctionPincode = auctionResponse.data.pincode ? String(auctionResponse.data.pincode).trim() : null;
                  fetchAvailableQAs(auctionPincode, auctionResponse.data.city, auctionResponse.data.state);
                }
              }
            } else {
              // If no seller address, try to fetch QAs with auction location
              const auctionPincode = auctionResponse.data.pincode ? String(auctionResponse.data.pincode).trim() : null;
              console.log('No seller address, using auction location:', { 
                pincode: auctionPincode, 
                city: auctionResponse.data.city, 
                state: auctionResponse.data.state 
              });
              if (auctionPincode || auctionResponse.data.city || auctionResponse.data.state) {
                fetchAvailableQAs(auctionPincode, auctionResponse.data.city, auctionResponse.data.state);
              } else {
                console.warn('No location data available to fetch QAs');
              }
            }
          } else {
            console.warn('Seller profile response was not successful or data is missing:', sellerResponse);
          }
        } catch (err) {
          console.error('Error fetching seller profile:', err);
          // Try to get seller info from auction data if profile fetch fails
          if (auctionResponse.data.seller_email || auctionResponse.data.seller_phone) {
            console.log('Using seller info from auction data as fallback');
          }
          // Still try to fetch QAs with auction location
          const auctionPincode = auctionResponse.data.pincode ? String(auctionResponse.data.pincode).trim() : null;
          if (auctionPincode || auctionResponse.data.city || auctionResponse.data.state) {
            fetchAvailableQAs(auctionPincode, auctionResponse.data.city, auctionResponse.data.state);
          }
        }
      } else {
        console.warn('No user_login_id found in auction data');
        // Still try to fetch QAs with auction location
        const auctionPincode = auctionResponse.data.pincode ? String(auctionResponse.data.pincode).trim() : null;
        if (auctionPincode || auctionResponse.data.city || auctionResponse.data.state) {
          fetchAvailableQAs(auctionPincode, auctionResponse.data.city, auctionResponse.data.state);
        }
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load auction details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableQAs = async (pincode, city, state) => {
    try {
      setLoadingQAs(true);
      let inspectors = [];
      let matchLevel = null;

      console.log('Fetching QAs with location:', { pincode, city, state });

      // Priority 1: Try pincode match
      if (pincode) {
        try {
          const response = await CommonService.get(API_URLS.AUCTION.GET_QUALITY_INSPECTORS, {
            pincode: pincode
          });
          console.log('Pincode match response:', response);
          // Handle both response.data (array) and response.data.data (nested) formats
          const inspectorsData = response?.data?.data || response?.data || [];
          if (response && response.success && Array.isArray(inspectorsData) && inspectorsData.length > 0) {
            inspectors = inspectorsData;
            matchLevel = 'pincode';
            console.log(`Found ${inspectors.length} QAs by pincode`);
          } else {
            console.log('No QAs found by pincode, trying city...');
          }
        } catch (error) {
          console.error('Error fetching QAs by pincode:', error);
          console.error('Error details:', error.response?.data || error.message);
        }
      }

      // Priority 2: If no pincode match, try city match
      if (inspectors.length === 0 && city) {
        try {
          const response = await CommonService.get(API_URLS.AUCTION.GET_QUALITY_INSPECTORS, {
            city: city
          });
          console.log('City match response:', response);
          const inspectorsData = response?.data?.data || response?.data || [];
          if (response && response.success && Array.isArray(inspectorsData) && inspectorsData.length > 0) {
            inspectors = inspectorsData;
            matchLevel = 'city';
            console.log(`Found ${inspectors.length} QAs by city`);
          } else {
            console.log('No QAs found by city, trying state...');
          }
        } catch (error) {
          console.error('Error fetching QAs by city:', error);
          console.error('Error details:', error.response?.data || error.message);
        }
      }

      // Priority 3: If no city match, try state match
      if (inspectors.length === 0 && state) {
        try {
          const response = await CommonService.get(API_URLS.AUCTION.GET_QUALITY_INSPECTORS, {
            state: state
          });
          console.log('State match response:', response);
          const inspectorsData = response?.data?.data || response?.data || [];
          if (response && response.success && Array.isArray(inspectorsData)) {
            inspectors = inspectorsData;
            matchLevel = 'state';
            console.log(`Found ${inspectors.length} QAs by state`);
          } else {
            console.log('No QAs found by state');
          }
        } catch (error) {
          console.error('Error fetching QAs by state:', error);
          console.error('Error details:', error.response?.data || error.message);
        }
      }

      console.log('Final QAs result:', { count: inspectors.length, matchLevel });
      setAvailableQAs(inspectors);
      setQaMatchLevel(matchLevel);
    } catch (error) {
      console.error('Error fetching available QAs:', error);
      setAvailableQAs([]);
      setQaMatchLevel(null);
    } finally {
      setLoadingQAs(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChecklistToggle = (item) => {
    setFormData(prev => ({
      ...prev,
      inspection_checklist: prev.inspection_checklist.includes(item)
        ? prev.inspection_checklist.filter(i => i !== item)
        : [...prev.inspection_checklist, item]
    }));
  };

  const handleAddChecklistItem = () => {
    if (customChecklistItem.trim()) {
      setFormData(prev => ({
        ...prev,
        inspection_checklist: [...prev.inspection_checklist, customChecklistItem.trim()]
      }));
      setCustomChecklistItem('');
    }
  };

  const handleRemoveChecklistItem = (item) => {
    setFormData(prev => ({
      ...prev,
      inspection_checklist: prev.inspection_checklist.filter(i => i !== item)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get seller location if not in formData
    let inspectionLocationAddress = formData.inspection_location_address;
    let inspectionLocationCity = formData.inspection_location_city;
    let inspectionLocationState = formData.inspection_location_state;
    let inspectionLocationPincode = formData.inspection_location_pincode || null;
    
    // If location not provided, use seller's address
    if (!inspectionLocationAddress && sellerProfile && sellerProfile.address) {
      try {
        const address = typeof sellerProfile.address === 'string' 
          ? JSON.parse(sellerProfile.address) 
          : sellerProfile.address;
        
        const addressParts = [];
        if (address.address) addressParts.push(address.address);
        if (address.landmark && address.landmark !== 'N/A') addressParts.push(address.landmark);
        inspectionLocationAddress = addressParts.join(', ');
        inspectionLocationCity = address.city || inspectionLocationCity || '';
        inspectionLocationState = address.state || inspectionLocationState || '';
        inspectionLocationPincode = address.pincode ? String(address.pincode).trim() : inspectionLocationPincode;
      } catch (e) {
        console.error('Error parsing seller address:', e);
      }
    }
    
    // Extract pincode from address string if not already set
    if (!inspectionLocationPincode && inspectionLocationAddress) {
      const pincodeMatch = inspectionLocationAddress.match(/\b\d{6}\b/);
      if (pincodeMatch) {
        inspectionLocationPincode = pincodeMatch[0];
      }
    }
    
    // Also try to extract from auction pincode if available
    if (!inspectionLocationPincode && auction && auction.pincode) {
      inspectionLocationPincode = String(auction.pincode).trim();
    }
    
    // Final fallback: use the pincode from availableQAs if we found QAs by pincode
    if (!inspectionLocationPincode && qaMatchLevel === 'pincode' && availableQAs.length > 0) {
      // Try to get pincode from the first QA's location
      const firstQA = availableQAs[0];
      if (firstQA && firstQA.pincode) {
        inspectionLocationPincode = String(firstQA.pincode).trim();
        console.log('Using pincode from matched QA:', inspectionLocationPincode);
      }
    }
    
    if (!formData.preferred_date || !formData.preferred_time || !inspectionLocationAddress) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.inspection_checklist.length === 0) {
      toast.error('Please add at least one inspection requirement');
      return;
    }

    try {
      setSubmitting(true);
      
      console.log('Submitting RFQ with location data:', {
        address: inspectionLocationAddress,
        city: inspectionLocationCity,
        state: inspectionLocationState,
        pincode: inspectionLocationPincode
      });
      
      const payload = {
        auction_id: parseInt(auctionId),
        winning_bid_id: winningBid.bid_id,
        preferred_date: formData.preferred_date,
        preferred_time: formData.preferred_time,
        inspection_location_address: inspectionLocationAddress,
        inspection_location_city: inspectionLocationCity || null,
        inspection_location_state: inspectionLocationState || null,
        inspection_location_pincode: inspectionLocationPincode || null,
        inspection_checklist: formData.inspection_checklist,
        special_instructions: formData.special_instructions || null
      };

      const response = await CommonService.post(API_URLS.AUCTION.CREATE_INSPECTION_RFQ, payload);
      
      if (response && response.success) {
        const notifiedCount = response.data?.notifiedInspectorsCount || 0;
        toast.success(`RFQ sent successfully to ${notifiedCount} inspector${notifiedCount !== 1 ? 's' : ''}!`);
        navigate(`/auction/${auctionId}/rfq-sent-success`, {
          state: { 
            rfqId: response.data.rfqId,
            notifiedCount: notifiedCount
          }
        });
      } else {
        throw new Error(response?.message || 'Failed to send RFQ');
      }
    } catch (error) {
      console.error('Error sending RFQ:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Failed to send RFQ');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return <div className="request-inspection-quotation loading">Loading...</div>;
  }

  if (!auction || !winningBid) {
    return <div className="request-inspection-quotation error">Auction or winning bid not found</div>;
  }

  return (
    <div className="request-inspection-quotation">
      <div className="container-fluid">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 8L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h1>Request Inspection Quotation</h1>
              <p>Send RFQ to qualified inspectors in your region</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-layout">
            {/* Left Column */}
            <div className="left-column">
              {/* Product Details */}
              <div className="section-card product-details-card">
                <div className="section-header">
                  <div className="section-icon orange">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <h3>Product Details</h3>
                </div>
                <div className="product-content">
                  <div className="product-image">
                    {getFirstProductImageUrl(auction) ? (
                      <img src={getFirstProductImageUrl(auction)} alt={auction.product_name} />
                    ) : (
                      <div className="placeholder-image">No Image</div>
                    )}
                  </div>
                  <div className="product-info">
                    <div className="product-name">{auction.product_name}</div>
                    <div className="product-description">{auction.product_description || 'No description available'}</div>
                    <div className="product-attributes">
                      <div className="attribute-row">
                        <div className="attribute-item">
                          <span className="label">Category:</span>
                          <span className="value">{auction.category_name || 'N/A'}</span>
                        </div>
                        {auction.product_attributes && (() => {
                          const attrs = typeof auction.product_attributes === 'string' 
                            ? JSON.parse(auction.product_attributes) 
                            : auction.product_attributes;
                          return attrs.condition ? (
                            <div className="attribute-item">
                              <span className="label">Condition:</span>
                              <span className="value">{attrs.condition}</span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                      <div className="attribute-row">
                        {auction.product_attributes && (() => {
                          const attrs = typeof auction.product_attributes === 'string' 
                            ? JSON.parse(auction.product_attributes) 
                            : auction.product_attributes;
                          return (
                            <>
                              {attrs.brand && (
                                <div className="attribute-item">
                                  <span className="label">Brand:</span>
                                  <span className="value">{attrs.brand}</span>
                                </div>
                              )}
                              {attrs.year && (
                                <div className="attribute-item">
                                  <span className="label">Year:</span>
                                  <span className="value">{attrs.year}</span>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Inspection Location */}
              <div className="section-card location-card">
                <div className="section-header">
                  <div className="section-icon green">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 1C6.24 1 4 3.24 4 6C4 10.5 10 17 10 17C10 17 16 10.5 16 6C16 3.24 13.76 1 10 1ZM10 8.25C8.76 8.25 7.75 7.24 7.75 6C7.75 4.76 8.76 3.75 10 3.75C11.24 3.75 12.25 4.76 12.25 6C12.25 7.24 11.24 8.25 10 8.25Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <h3>Inspection Location</h3>
                </div>
                <div className="location-content">
                  {(() => {
                    // Get seller address from seller profile
                    let sellerAddress = 'N/A';
                    let sellerCityState = '';
                    
                    if (sellerProfile && sellerProfile.address) {
                      try {
                        const address = typeof sellerProfile.address === 'string' 
                          ? JSON.parse(sellerProfile.address) 
                          : sellerProfile.address;
                        
                        // Build address string
                        const addressParts = [];
                        if (address.address) addressParts.push(address.address);
                        if (address.landmark && address.landmark !== 'N/A') addressParts.push(address.landmark);
                        sellerAddress = addressParts.length > 0 ? addressParts.join(', ') : 'N/A';
                        
                        // Build city/state string
                        const locationParts = [];
                        if (address.city) locationParts.push(address.city);
                        if (address.state) locationParts.push(address.state);
                        if (address.pincode) locationParts.push(address.pincode);
                        sellerCityState = locationParts.join(', ');
                      } catch (e) {
                        console.error('Error parsing seller address:', e);
                        // Fallback to auction address
                        sellerAddress = auction.address || 'N/A';
                        sellerCityState = [auction.city, auction.state].filter(Boolean).join(', ');
                      }
                    } else {
                      // Fallback to auction address if seller profile not loaded
                      sellerAddress = auction.address || 'N/A';
                      sellerCityState = [auction.city, auction.state].filter(Boolean).join(', ');
                    }
                    
                    return (
                      <>
                        <div className="address">{sellerAddress}</div>
                        {sellerCityState && <div className="city-state">{sellerCityState}</div>}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Location Matching Priority Info */}
              <div className="section-card location-matching-info-card">
                <div className="section-header">
                  <div className="section-icon blue">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM11 14H9V12H11V14ZM11 10H9V6H11V10Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <h3>Quality Inspector Matching</h3>
                </div>
                <div className="matching-info-content">
                  <p className="info-title">While listing Quality Inspectors, apply location-based priority matching in the following order:</p>
                  <ol className="matching-priority-list">
                    <li className={qaMatchLevel === 'pincode' ? 'active' : ''}>
                      <span className="priority-number">1</span>
                      <span className="priority-text">Match by inspection location <strong>pincode</strong></span>
                      {qaMatchLevel === 'pincode' && (
                        <span className="match-badge">Matched</span>
                      )}
                    </li>
                    <li className={qaMatchLevel === 'city' ? 'active' : ''}>
                      <span className="priority-number">2</span>
                      <span className="priority-text">If none are available, match by <strong>city</strong></span>
                      {qaMatchLevel === 'city' && (
                        <span className="match-badge">Matched</span>
                      )}
                    </li>
                    <li className={qaMatchLevel === 'state' ? 'active' : ''}>
                      <span className="priority-number">3</span>
                      <span className="priority-text">If still unavailable, match by <strong>state</strong></span>
                      {qaMatchLevel === 'state' && (
                        <span className="match-badge">Matched</span>
                      )}
                    </li>
                  </ol>
                  
                  {/* Available QAs Count */}
                  {loadingQAs ? (
                    <div className="qa-count-loading">Loading available Quality Inspectors...</div>
                  ) : availableQAs.length > 0 ? (
                    <div className="qa-count-display">
                      <div className="qa-count-header">
                        <span className="qa-count-label">Available Quality Inspectors:</span>
                        <span className="qa-count-value">{availableQAs.length}</span>
                      </div>
                      <div className="qa-match-level">
                        Matched by: <strong>{qaMatchLevel === 'pincode' ? 'Pincode' : qaMatchLevel === 'city' ? 'City' : 'State'}</strong>
                      </div>
                    </div>
                  ) : (
                    <div className="qa-count-display no-qa">
                      <span>No Quality Inspectors available in this location</span>
                    </div>
                  )}
                  
                  <p className="info-footer">Quality Inspectors will be displayed based on this priority logic.</p>
                </div>
              </div>

              {/* Seller and Buyer Details Row */}
              <div className="details-row">
                {/* Seller Details */}
                <div className="section-card seller-details-card">
                  <div className="section-header">
                    <div className="section-icon orange">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10 9C11.6569 9 13 7.65685 13 6C13 4.34315 11.6569 3 10 3C8.34315 3 7 4.34315 7 6C7 7.65685 8.34315 9 10 9Z" fill="currentColor"/>
                        <path d="M3 17C3 13.6863 5.68629 11 9 11H11C14.3137 11 17 13.6863 17 17V18H3V17Z" fill="currentColor"/>
                      </svg>
                    </div>
                    <h3>Seller Details</h3>
                  </div>
                  <div className="company-name">{auction.seller_company || auction.seller_name || 'N/A'}</div>
                  <div className="role-subtitle">{auction.seller_company || 'Seller'}</div>
                  {(() => {
                    // Try multiple sources for phone
                    let sellerPhone = 'N/A';
                    if (sellerProfile) {
                      // First try user_phone from user_login join
                      if (sellerProfile.user_phone) {
                        sellerPhone = sellerProfile.user_phone;
                      } 
                      // Then try contact_details JSON
                      else if (sellerProfile.contact_details) {
                        try {
                          const contactDetails = typeof sellerProfile.contact_details === 'string' 
                            ? JSON.parse(sellerProfile.contact_details) 
                            : sellerProfile.contact_details;
                          if (contactDetails?.contact_number) {
                            sellerPhone = contactDetails.contact_number;
                          }
                        } catch (e) {
                          console.error('Error parsing contact_details:', e);
                        }
                      }
                      // Then try direct phone fields
                      if (sellerPhone === 'N/A' && (sellerProfile.phone || sellerProfile.contact_phone)) {
                        sellerPhone = sellerProfile.phone || sellerProfile.contact_phone;
                      }
                    }
                    // Fallback to auction data
                    if (sellerPhone === 'N/A' && (auction.seller_phone || auction.seller_contact_phone)) {
                      sellerPhone = auction.seller_phone || auction.seller_contact_phone;
                    }

                    // Try multiple sources for email
                    let sellerEmail = 'N/A';
                    if (sellerProfile) {
                      // First try user_email from user_login join
                      if (sellerProfile.user_email) {
                        sellerEmail = sellerProfile.user_email;
                      } 
                      // Then try contact_details JSON
                      else if (sellerProfile.contact_details) {
                        try {
                          const contactDetails = typeof sellerProfile.contact_details === 'string' 
                            ? JSON.parse(sellerProfile.contact_details) 
                            : sellerProfile.contact_details;
                          if (contactDetails?.contact_email) {
                            sellerEmail = contactDetails.contact_email;
                          }
                        } catch (e) {
                          console.error('Error parsing contact_details:', e);
                        }
                      }
                      // Then try direct email field
                      if (sellerEmail === 'N/A' && sellerProfile.email) {
                        sellerEmail = sellerProfile.email;
                      }
                    }
                    // Fallback to auction data
                    if (sellerEmail === 'N/A' && (auction.seller_email || auction.seller_contact_email)) {
                      sellerEmail = auction.seller_email || auction.seller_contact_email;
                    }

                    return (
                      <>
                        <div className="contact-item">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122L9.65 12.1a.678.678 0 0 1-.64-.39l-.84-2.1a.678.678 0 0 1-.122-.58l.488-2.307a.678.678 0 0 0-.122-.58L6.33 1.328a.678.678 0 0 0-1.015-.063z" fill="currentColor"/>
                          </svg>
                          <span>{sellerPhone}</span>
                        </div>
                        <div className="contact-item">
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.879-5.64-3.19L2 12.105V5.383l5.326 3.19L13.966 8.262Z" fill="currentColor"/>
                          </svg>
                          <span>{sellerEmail}</span>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Buyer Details */}
                <div className="section-card buyer-details-card">
                  <div className="section-header">
                    <div className="section-icon blue">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M10 9C11.6569 9 13 7.65685 13 6C13 4.34315 11.6569 3 10 3C8.34315 3 7 4.34315 7 6C7 7.65685 8.34315 9 10 9Z" fill="currentColor"/>
                        <path d="M3 17C3 13.6863 5.68629 11 9 11H11C14.3137 11 17 13.6863 17 17V18H3V17Z" fill="currentColor"/>
                      </svg>
                    </div>
                    <h3>Buyer Details</h3>
                  </div>
                  <div className="company-name">{buyerProfile?.company_name || 'N/A'}</div>
                  <div className="contact-person-name">{buyerProfile?.user_name || formData.contact_name || 'N/A'}</div>
                  <div className="contact-item">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122L9.65 12.1a.678.678 0 0 1-.64-.39l-.84-2.1a.678.678 0 0 1-.122-.58l.488-2.307a.678.678 0 0 0-.122-.58L6.33 1.328a.678.678 0 0 0-1.015-.063z" fill="currentColor"/>
                    </svg>
                    <span>{formData.contact_phone || 'N/A'}</span>
                  </div>
                  <div className="contact-item">
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.879-5.64-3.19L2 12.105V5.383l5.326 3.19L13.966 8.262Z" fill="currentColor"/>
                    </svg>
                    <span>{formData.contact_email || 'N/A'}</span>
                  </div>
                </div>
              </div>

              {/* Inspection Checklist */}
              <div className="section-card checklist-card">
                <div className="section-header">
                  <div className="section-icon green">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M16.667 5L7.5 14.167 3.333 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3>Inspection Checklist</h3>
                </div>
                <div className="checklist-items">
                  {formData.inspection_checklist.map((item, index) => (
                    <div key={index} className="checklist-item">
                      <div className="check-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13.333 4L6 11.333 2.667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <span className="checklist-text">{item}</span>
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => handleRemoveChecklistItem(item)}
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="add-checklist-item">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Add inspection requirement..."
                    value={customChecklistItem}
                    onChange={(e) => setCustomChecklistItem(e.target.value)}
                  />
                  <button type="button" className="btn-add" onClick={handleAddChecklistItem}>
                    + Add
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="right-column">
              {/* Requested By */}
              <div className="section-card requested-by-card">
                <h3>Requested By</h3>
                <div className="company-name">{buyerProfile?.company_name || 'N/A'}</div>
                <div className="role">(Buyer)</div>
              </div>

              {/* Inspection Schedule */}
              <div className="section-card schedule-card">
                <div className="section-header">
                  <div className="section-icon blue">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <rect x="3" y="4" width="14" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
                      <line x1="3" y1="8" x2="17" y2="8" stroke="currentColor" strokeWidth="2"/>
                      <line x1="7" y1="2" x2="7" y2="6" stroke="currentColor" strokeWidth="2"/>
                      <line x1="13" y1="2" x2="13" y2="6" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <h3>Inspection Schedule</h3>
                </div>
                <div className="form-group">
                  <label>Preferred Date <span className="required">*</span></label>
                  <div className="input-with-icon">
                    <input
                      type="date"
                      className="form-control"
                      name="preferred_date"
                      value={formData.preferred_date}
                      onChange={handleInputChange}
                      placeholder="dd-mm-yyyy"
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Preferred Time <span className="required">*</span></label>
                  <div className="input-with-icon">
                    <input
                      type="time"
                      className="form-control"
                      name="preferred_time"
                      value={formData.preferred_time}
                      onChange={handleInputChange}
                      placeholder="--:-- --"
                      required
                    />
                  </div>
                </div>
                <div className="info-note">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="2"/>
                    <polyline points="8 4 8 8 11 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>Inspection must be completed within 72 hours of scheduling</span>
                </div>
              </div>

              {/* Contact Person */}
              <div className="section-card contact-card">
                <div className="section-header">
                  <div className="section-icon orange">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M10 9C11.6569 9 13 7.65685 13 6C13 4.34315 11.6569 3 10 3C8.34315 3 7 4.34315 7 6C7 7.65685 8.34315 9 10 9Z" fill="currentColor"/>
                      <path d="M3 17C3 13.6863 5.68629 11 9 11H11C14.3137 11 17 13.6863 17 17V18H3V17Z" fill="currentColor"/>
                    </svg>
                  </div>
                  <h3>Contact Person</h3>
                </div>
                <div className="form-group">
                  <label>Name <span className="required">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone <span className="required">*</span></label>
                  <div className="input-with-icon">
                    <svg className="input-icon-left" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M3.654 1.328a.678.678 0 0 0-1.015-.063L1.605 2.3c-.483.484-.661 1.169-.45 1.77a17.568 17.568 0 0 0 4.168 6.608 17.569 17.569 0 0 0 6.608 4.168c.601.211 1.286.033 1.77-.45l1.034-1.034a.678.678 0 0 0-.063-1.015l-2.307-1.794a.678.678 0 0 0-.58-.122L9.65 12.1a.678.678 0 0 1-.64-.39l-.84-2.1a.678.678 0 0 1-.122-.58l.488-2.307a.678.678 0 0 0-.122-.58L6.33 1.328a.678.678 0 0 0-1.015-.063z" fill="currentColor"/>
                    </svg>
                    <input
                      type="tel"
                      className="form-control"
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <div className="input-with-icon">
                    <svg className="input-icon-left" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.879-5.64-3.19L2 12.105V5.383l5.326 3.19L13.966 8.262Z" fill="currentColor"/>
                    </svg>
                    <input
                      type="email"
                      className="form-control"
                      name="contact_email"
                      value={formData.contact_email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Special Instructions */}
              <div className="section-card instructions-card">
                <div className="section-header">
                  <div className="section-icon grey">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                      <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <h3>Special Instructions</h3>
                </div>
                <textarea
                  className="form-control"
                  name="special_instructions"
                  rows="4"
                  placeholder="Add any specific requirements or instructions for the inspector..."
                  value={formData.special_instructions}
                  onChange={handleInputChange}
                />
              </div>

              {/* Submit Button */}
              <button type="submit" className="btn-submit" disabled={submitting}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M18 2L9 11M18 2L12 18L9 11M18 2L2 8L9 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {submitting ? 'Sending...' : 'Send RFQ to Inspectors'}
              </button>

              {/* Important Note */}
              <div className="important-note">
                <div className="note-icon">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 2C5.58 2 2 5.58 2 10C2 14.42 5.58 18 10 18C14.42 18 18 14.42 18 10C18 5.58 14.42 2 10 2ZM11 14H9V12H11V14ZM11 10H9V6H11V10Z" fill="currentColor"/>
                  </svg>
                </div>
                <div className="note-content">
                  <strong>Important</strong>
                  <p>This RFQ will be sent to all qualified inspectors in your region. You'll receive multiple quotations within 24 hours. Review and select the best inspector for your needs.</p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
